import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Singleton full-screen learn-more panel. Rendered once outside the masked
 * scroll container; the configurator passes the product handle on each open.
 *
 * Visual state is driven by a single `data-state` attribute — JS toggles it:
 *   (absent)            → invisible (visibility:hidden, pointer-events:none)
 *   data-state="loading" → dark overlay + centered spinner
 *   data-state="open"    → content wipes in, footer fades in, overlay releases after
 *   data-state="closing" → exit transition in progress
 *
 * On close, state moves to "closing" (content fades out), then the attribute
 * is removed. The overlay/spinner never flash back — "loading" is only set
 * when a fetch is needed.
 *
 * Uses WAI-ARIA APG modal dialog pattern with:
 *   - `inert` on siblings (prevents AT browsing outside the panel)
 *   - Cyclical focus trap via trapFocus (APG Tab cycling requirement)
 *   - Scroll lock on body (iOS scroll-through prevention)
 *
 * @extends HTMLElement
 */
class LearnMorePanel extends HTMLElement {
  /** @type {HTMLElement|null} Element that opened the panel (for focus return) */
  #triggerEl = null;

  /** @type {number} Scroll position before opening (for scroll lock restore) */
  #previousScrollY = 0;

  /** @type {AbortController|null} Lifecycle controller for element-level listeners */
  #controller = null;

  /** @type {AbortController|null} Active while panel is open (Escape listener) */
  #openController = null;

  /** @type {AbortController|null} Per-fetch controller — aborted on close or new fetch */
  #fetchController = null;

  /** @type {Map<string, string>} Cached section HTML keyed by product handle */
  #cache = new Map();

  /** @type {HTMLButtonElement|null} */
  #closeBtn = null;

  /** @type {HTMLElement|null} */
  #contentEl = null;

  /** @type {HTMLElement|null} */
  #statusEl = null;

  connectedCallback() {
    this.#closeBtn = this.querySelector('[data-close-panel]');
    this.#contentEl = this.querySelector('[data-learn-more-content]');
    this.#statusEl = this.querySelector('[data-panel-status]');

    this.#controller = new AbortController();

    if (this.#closeBtn) {
      this.#closeBtn.addEventListener(
        'click',
        () => this.close(),
        { signal: this.#controller.signal }
      );
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#openController?.abort();
    this.#fetchController?.abort();
    this.#setBackgroundInert(false);
    removeTrapFocus();
  }

  /**
   * Open the panel for a given product.
   * @param {HTMLElement} [triggerEl] - Element to return focus to on close
   * @param {string} handle - Product handle for the Section Rendering API fetch
   * @param {string} [label] - Accessible label (dynamic — singleton serves many products)
   */
  open(triggerEl, handle, label) {
    if (!handle) return;

    this.#triggerEl = triggerEl || null;

    if (label) {
      this.setAttribute('aria-label', label);
    }

    // Scroll lock — prevents iOS body scroll-through behind fixed overlay.
    // Stores scrollY so we can restore the user's position on close.
    this.#previousScrollY = window.scrollY;
    document.body.style.setProperty('width', '100%');
    document.body.style.setProperty('position', 'fixed');
    document.body.style.setProperty('top', `-${this.#previousScrollY}px`);

    // Bind Escape only while open
    this.#openController?.abort();
    this.#openController = new AbortController();
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
        }
      },
      { signal: this.#openController.signal }
    );

    this.#setBackgroundInert(true);
    this.#showContent(handle);
    this.#trapAndFocusClose();
  }

  /**
   * Close the panel. Content fades out via "closing" state, then the attribute
   * is removed. If closed during loading, cleanup is instant.
   */
  async close() {
    this.#fetchController?.abort();
    this.#fetchController = null;
    this.#openController?.abort();
    this.#openController = null;
    removeTrapFocus();

    const state = this.dataset.state;

    if (state !== 'open') {
      this.#cleanup();
      return;
    }

    this.dataset.state = 'closing';

    if (this.#contentEl) {
      await onAnimationEnd(this.#contentEl, undefined, { subtree: false });
    }

    this.#cleanup();
  }

  /** Reset panel to its hidden, inactive state. */
  #cleanup() {
    delete this.dataset.state;
    this.#setBackgroundInert(false);
    this.#announce('');

    document.body.style.removeProperty('width');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    this.#triggerEl?.focus();
  }

  /**
   * Show content for a product handle. Serves from cache if available,
   * otherwise shows overlay+spinner and fetches via Section Rendering API.
   * @param {string} handle
   */
  async #showContent(handle) {
    if (!this.#contentEl) return;

    const cached = this.#cache.get(handle);
    if (cached) {
      this.#contentEl.innerHTML = cached;
      this.dataset.state = 'open';
      this.#trapAndFocusClose();
      return;
    }

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    this.#contentEl.innerHTML = '';
    this.dataset.state = 'loading';
    this.#announce(this.dataset.textLoading || 'Loading product details');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(
        `${root}products/${handle}?sections=lq_product-learn-more`,
        { signal: this.#fetchController.signal }
      );

      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);

      const data = await response.json();
      const html = data['lq_product-learn-more'];

      if (!html) throw new Error('Section not found in response');

      this.#cache.set(handle, html);
      this.#contentEl.innerHTML = html;
      this.dataset.state = 'open';
      this.#trapAndFocusClose();
      this.#announce(this.dataset.textLoaded || 'Product details loaded');
    } catch (error) {
      if (error.name === 'AbortError') return;

      this.#contentEl.innerHTML =
        `<div class="product-info-modal__error">${this.dataset.textErrorMessage || 'Unable to load product details. Please try again.'}</div>`;
      this.dataset.state = 'open';
      this.#trapAndFocusClose();
      this.#announce(this.dataset.textError || 'Unable to load product details');
    }
  }

  /**
   * (Re-)apply cyclical focus trap and move focus to the close button.
   * Called on initial open and again after content loads so the trap's
   * internal first/last references include newly injected focusable elements.
   */
  #trapAndFocusClose() {
    trapFocus(this);
    this.#closeBtn?.focus();
  }

  /**
   * Toggle inert on sibling elements so assistive tech (e.g. VoiceOver swipe)
   * cannot browse background content. Complements trapFocus which handles
   * cyclical Tab key trapping per the APG modal dialog pattern.
   * @param {boolean} inert
   */
  #setBackgroundInert(inert) {
    const parent = this.parentElement;
    if (!parent) return;

    for (const sibling of parent.children) {
      if (sibling === this) continue;
      sibling.toggleAttribute('inert', inert);
    }
  }

  /** @param {string} message */
  #announce(message) {
    if (this.#statusEl) this.#statusEl.textContent = message;
  }
}

if (!customElements.get('learn-more-panel')) {
  customElements.define('learn-more-panel', LearnMorePanel);
}
